import React, { useEffect, useMemo, useState } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { DepartmentService } from '../../services/department.service';
import { DepartmentTreeNodeDTO, DepartmentUnitType } from '../../interfaces/department.interface';
import SearchableSelect from './SearchableSelect';

/** A flattened, path-labelled org-unit option ("Floor 2 / Sewing / Line 7"). */
export interface OrgUnitOption {
    id: string;
    name: string;
    code: string | null;
    unitType: DepartmentUnitType;
    path: string;
}

/**
 * Depth-first flatten of a nested department tree (as returned by
 * `GET department/institution/{id}/tree`) into path-labelled options, root -> leaf, " / "-joined.
 */
export const flattenOrgUnitTree = (nodes: DepartmentTreeNodeDTO[], parentPath = ''): OrgUnitOption[] => {
    const result: OrgUnitOption[] = [];
    for (const node of nodes) {
        const path = parentPath ? `${parentPath} / ${node.name}` : node.name;
        result.push({ id: node.id, name: node.name, code: node.code, unitType: node.unitType, path });
        if (node.children && node.children.length) {
            result.push(...flattenOrgUnitTree(node.children, path));
        }
    }
    return result;
};

/**
 * Finds `rootId` in a nested department tree and returns its id plus every descendant's id
 * (root first, depth-first). Used to keep a unit from being re-parented under its own descendant
 * -- the backend rejects that with a 400, so the parent picker should never offer it in the first
 * place. Returns an empty array if `rootId` isn't found.
 */
export const collectSubtreeIds = (nodes: DepartmentTreeNodeDTO[], rootId: string): string[] => {
    const collectAll = (node: DepartmentTreeNodeDTO): string[] => [
        node.id,
        ...node.children.flatMap(collectAll),
    ];
    for (const node of nodes) {
        if (node.id === rootId) return collectAll(node);
        const found = collectSubtreeIds(node.children, rootId);
        if (found.length) return found;
    }
    return [];
};

interface OrgUnitPickerProps {
    institutionId: string;
    /** Selected unit id (not the option object) -- easier for callers that just persist an id. */
    value: string | null;
    onChange: (value: OrgUnitOption | null) => void;
    /** Restrict selectable options to these unit types (e.g. rank-filtered parent choices). */
    types?: DepartmentUnitType[];
    /** Exclude these unit ids (e.g. the unit being edited, so it can't be its own parent). */
    excludeIds?: string[];
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: SxProps<Theme>;
    onError?: (error: unknown) => void;
    'data-testid'?: string;
}

/**
 * Shared org-unit (Department/Floor/Section/Line) picker: a searchable select over the flattened
 * institution org tree, each option labelled by its full path
 * (docs/decisions/2026-09-11-garments-org-hierarchy.md §2 "Frontend"). Used by
 * `DepartmentForm`'s parent picker (rank-filtered via `types`) and the wage-sheet org-unit filter.
 *
 * Frontend UI convention: filter/toolbar controls must never occupy a whole row on their own --
 * this component defaults to `fullWidth={true}` (it simply fills its own flex cell); callers in a
 * filter row constrain that cell via `sx` on their end, not by disabling `fullWidth` here.
 */
export const OrgUnitPicker: React.FC<OrgUnitPickerProps> = ({
    institutionId,
    value,
    onChange,
    types,
    excludeIds,
    label = 'Org Unit',
    placeholder = 'Search departments, floors, sections, lines...',
    size = 'small',
    fullWidth = true,
    disabled = false,
    required,
    error,
    helperText,
    sx,
    onError,
    ...rest
}) => {
    const [tree, setTree] = useState<DepartmentTreeNodeDTO[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!institutionId) {
            setTree([]);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const data = await DepartmentService.getDepartmentTree(institutionId);
                if (!cancelled) setTree(data);
            } catch (err) {
                if (!cancelled) {
                    setTree([]);
                    onError?.(err);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const flat = useMemo(() => flattenOrgUnitTree(tree), [tree]);

    const selectedOption = useMemo(
        () => (value ? flat.find(o => o.id === value) ?? null : null),
        [flat, value]
    );

    const options = useMemo(() => {
        const excluded = new Set(excludeIds ?? []);
        const filtered = flat.filter(o => (!types || types.includes(o.unitType)) && !excluded.has(o.id));
        if (selectedOption && !filtered.some(o => o.id === selectedOption.id)) {
            return [selectedOption, ...filtered];
        }
        return filtered;
    }, [flat, types, excludeIds, selectedOption]);

    return (
        <SearchableSelect<OrgUnitOption>
            options={options}
            value={value}
            selectedOption={selectedOption}
            onChange={(_id, option) => onChange(option)}
            getOptionLabel={option => option.path}
            loading={loading}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            sx={sx}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
            noOptionsText={loading ? 'Loading…' : 'No org units'}
            {...rest}
        />
    );
};

export default OrgUnitPicker;
